// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Recording a change to a machine, in two steps (design doc §8.5, §8.6, US-B-01…04).
 *
 * **Why the reason comes first.** It is the one field that describes the event rather than
 * the parts, and asking for it last turns it into a box to clear on the way out — which is
 * how maintenance logs fill up with "换件". Asked first, while the technician still has in
 * mind what sent them to the machine, it is the sentence someone reads a year later when
 * the same joint fails again.
 *
 * **Why the parts are ticked, not typed.** Step two lists what is actually on this machine
 * and the technician ticks the components they touched. The server matches a REPLACE or
 * REMOVE on `position_id`, so a typed position that does not resolve is a rejected
 * submission they then have to decode; and the list doubles as the answer to "what was in
 * there before", which is the thing being replaced.
 *
 * **Why an unticked row has no fields.** When every row carried an action select reading
 * 更换 and a live serial box, four hundred rows read as four hundred replacements about to
 * be submitted. A row says what is fitted until it is ticked; only then does it ask what
 * was done to it. What will be submitted is exactly the ticked rows and the added parts,
 * and the bar at the bottom counts them.
 *
 * Adding a part the BOM never had is a separate affordance rather than the default. §8.5
 * allows it — a machine drifting from its baseline is normal — but it is the rarer case,
 * and mixing it into the same list would invite a new position where an existing one was
 * meant. Its button sits in the toolbar, so nobody scrolls past the whole machine to find it.
 *
 * The whole event is submitted at once, because the server writes one version per event
 * (§7.4). Line-by-line submission would produce a version per line and a history in which
 * no single entry describes what the technician actually did.
 *
 * **Modules** (component design §9.5). The list is the complete archive: a module slot
 * opens onto the module's own parts, and a part ticked there is a line for that module's
 * archive (`target_archive_id`), which the server versions along with everything above it.
 * A module slot itself is replaced by scanning the new module's code, or removed — and a
 * module taken out cannot also have its insides changed in the same submission, which is
 * said here before the server says it.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import type {
  ArchiveDetail,
  ArchiveItem,
  MaintenanceSubmissionView,
  ModuleLookup,
  ModuleRef,
} from "@/api/types";
import { filedAfter, occurredAtNote } from "@/archive/changeTime";
import { materialInfoOf } from "@/archive/partState";
import { foldNodes, inBomOrder } from "@/archive/tree";
import { AddLabel } from "@/components/AddLabel";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { problemText } from "@/components/ModuleSlotsPanel";
import { useLeaveGuard } from "@/hooks/useLeaveGuard";
import { useBarcodeRules } from "@/scanner/barcodeRules";
import { type FitProblem, fitProblem } from "@/scanner/moduleCodes";
import { scanEnterHandlers } from "@/scanner/scanEnter";
import { isUsableSerial, normalizeSerial } from "@/scanner/serials";
import { type ModuleLookupState, useModuleLookups } from "@/scanner/useModuleLookups";
import { useSerialStatus } from "@/scanner/useSerialStatus";
import { foldedToLevel, hiddenKeys, levelCount, parentKeys } from "@/tree/fold";
import { type ChangeType, ChangeTypeSelect, needsDescription } from "./ChangeType";
import { DateTimeField, timeZoneLabel } from "./DateTimeField";
import { FoldControls } from "./FoldControls";
import { ChevronIcon } from "./icons";
import { isValidJiraUrl } from "./JiraLink";
import { Modal } from "./Modal";
import { PendingCorrectionNotice } from "./PendingCorrectionNotice";
import { ResizableTable } from "./ResizableTable";
import { ScrappedSerialPrompt } from "./ScrappedSerialPrompt";
import { BarcodeMismatchNote, type NoteState, SerialStatusNote } from "./SerialStatusNote";

type Action = "REPLACE" | "REMOVE";

/** What the code in a module slot's box says about the module it names. */
type ModuleNote =
  | ModuleLookupState
  | { status: "duplicate"; module: ModuleLookup }
  | { status: "refused"; module: ModuleLookup; problem: FitProblem };

export interface MaintenanceSubmission {
  changeType: ChangeType;
  /** The description, trimmed; null when none was given. */
  reason: string | null;
  /** The Jira ticket's address, trimmed; null when none was given. */
  jiraUrl: string | null;
  occurredAt: string | null;
  lines: {
    line_no: number;
    action: "REPLACE" | "REMOVE" | "ADD";
    position_id: string | null;
    new_position: {
      /** 物料描述 of the added part: the position's name, as on the list. */
      position_name: string;
      material_code: string | null;
    } | null;
    old_serial: string | null;
    new_serial: string | null;
    /** The module the line changes, for a part inside a module; absent for the open archive. */
    target_archive_id?: string;
    /** REPLACE only, and only when the part fitted is not the BOM's (blank = unchanged). */
    new_material_code: string | null;
    new_material_name: string | null;
    quantity: number;
  }[];
}

export interface Touched {
  action: Action;
  /** Serialized positions only. */
  newSerial: string;
  /**
   * Counted positions only: how many units the change covers, as typed. `null` until the
   * operator touches it, meaning "all of them" — what the dialog sent before the field
   * existed, now visible and changeable instead of implied.
   */
  quantity: string | null;
  /** Off unless the part fitted is not the one the BOM names; the fields below only count then. */
  substitute: boolean;
  newMaterialCode: string;
  newMaterialName: string;
}

const BLANK_TOUCHED: Touched = {
  action: "REPLACE",
  newSerial: "",
  quantity: null,
  substitute: false,
  newMaterialCode: "",
  newMaterialName: "",
};

/**
 * A counted position has no serial to exchange (§6.3) — the server refuses one with
 * NEW_SERIAL_NOT_ALLOWED. What identifies the change is how many units it covers.
 *
 * Keyed off `tracking_mode`, not off whether the archive has a BOM: counted positions exist
 * on both — every no-BOM entry filed without a serial, and every non-critical BOM line.
 */
function isCounted(item: ArchiveItem): boolean {
  return item.tracking_mode === "QUANTITY";
}

type PartKind = "all" | "critical" | "counted" | "selected";

const PART_KINDS: PartKind[] = ["all", "critical", "counted", "selected"];

/** The quantity a line will carry, or null when what was typed cannot be sent. */
function lineQuantity(item: ArchiveItem, line: Touched): number | null {
  if (!isCounted(item)) return item.quantity || 1;
  if (line.quantity === null) return item.quantity;
  const count = Number(line.quantity);
  // Bounded by what is fitted: more would be QUANTITY_EXCEEDS_CURRENT from the server, and
  // it is better learned while the field is still under the caret.
  return Number.isInteger(count) && count >= 1 && count <= item.quantity ? count : null;
}

export interface Added {
  position_name: string;
  /** Held as strings because they are form fields; blanks become null on submit. */
  material_code: string;
  serial: string;
  quantity: string;
}

const BLANK_ADDED: Added = {
  position_name: "",
  material_code: "",
  serial: "",
  quantity: "1",
};

/** An added part's quantity, or null when what was typed cannot be sent. A serial names
 * one physical unit, so a part with one is always exactly one. */
function addedQuantity(entry: Added): number | null {
  if (entry.serial.trim()) return 1;
  const count = Number(entry.quantity);
  return entry.quantity.trim() && Number.isInteger(count) && count >= 1 ? count : null;
}

/** Where a row's change goes (§9.5): a module fitted in the open archive, at some depth. */
interface InnerPlace {
  module: ModuleRef;
  /** 1 for a part of a module fitted directly in the archive, 2 one level further down. */
  level: number;
  /** The module slots above the row, outermost first — all open for it to show. */
  slots: string[];
  /** The modules above the row, outermost first: a change to it versions every one of
   *  them, so it cannot be dated before any of their filings (§8.6 step 6). */
  modules: ModuleRef[];
}

function isModuleSlot(item: ArchiveItem): boolean {
  return item.origin === "BOM_MODULE";
}

/** Every module's parts under its slot, outermost first, each with where it belongs. */
function innerRows(items: ArchiveItem[]): {
  places: Map<string, InnerPlace>;
  below: Map<string, ArchiveItem[]>;
} {
  const places = new Map<string, InnerPlace>();
  const below = new Map<string, ArchiveItem[]>();
  const walk = (
    slot: ArchiveItem,
    level: number,
    slots: string[],
    outer: ModuleRef[],
  ): ArchiveItem[] => {
    if (!slot.module || !slot.children?.length) return [];
    const modules = [...outer, slot.module];
    const collected: ArchiveItem[] = [];
    for (const child of inBomOrder(slot.children)) {
      places.set(child.position_id, { module: slot.module, level, slots, modules });
      collected.push(child, ...walk(child, level + 1, [...slots, child.position_id], modules));
    }
    below.set(slot.position_id, collected);
    return collected;
  };
  for (const item of items) walk(item, 1, [item.position_id], []);
  return { places, below };
}

/** What the form starts from when it corrects a submission (§8.6.3). */
export interface CorrectionDraft {
  title: string;
  changeType: ChangeType;
  reason: string;
  jiraUrl: string;
  /** As the time field holds it, `YYYY-MM-DDTHH:mm` local. */
  occurredAt: string;
  touched: Record<string, Touched>;
  added: Added[];
  /** Serials the record being corrected fitted: installed now only because of it, so not a
   *  conflict for the corrected version. Normalized. */
  ownSerials: ReadonlySet<string>;
}

function localMinute(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * A submission back in the form's own terms: each changed part ticked as it was, each added
 * part entered again. Null when it holds a line the form cannot express (an ADD onto an
 * existing position) — editing it here would silently drop that line, so only revoking is
 * offered.
 */
export function correctionDraft(
  view: MaintenanceSubmissionView,
  title: string,
): CorrectionDraft | null {
  const touched: Record<string, Touched> = {};
  const added: Added[] = [];
  const ownSerials = new Set<string>();
  for (const line of view.lines) {
    if (line.new_serial) ownSerials.add(normalizeSerial(line.new_serial));
    if (line.action === "ADD") {
      if (!line.new_position || line.target_archive_id) return null;
      added.push({
        position_name: line.new_position.position_name,
        material_code: line.new_position.material_code ?? "",
        serial: line.new_serial ?? "",
        quantity: String(line.quantity),
      });
      continue;
    }
    if (!line.position_id) return null;
    const substitute = Boolean(line.new_material_code || line.new_material_name);
    touched[line.position_id] = {
      action: line.action,
      newSerial: line.new_serial ?? "",
      quantity: String(line.quantity),
      substitute,
      newMaterialCode: line.new_material_code ?? "",
      newMaterialName: line.new_material_name ?? "",
    };
  }
  return {
    title,
    changeType: view.change_type as ChangeType,
    reason: view.reason ?? "",
    jiraUrl: view.jira_url ?? "",
    occurredAt: localMinute(view.occurred_at),
    touched,
    added,
    ownSerials,
  };
}

interface Props {
  archive: ArchiveDetail;
  pending: boolean;
  error: React.ReactNode;
  onSubmit: (submission: MaintenanceSubmission) => void;
  onClose: () => void;
  /** Correcting a submission: the form starts filled in, on the machine before it. */
  correcting?: CorrectionDraft;
}

export function MaintenanceDialog({
  archive,
  pending,
  error,
  onSubmit,
  onClose,
  correcting,
}: Props) {
  const { t, i18n } = useTranslation();
  const moduleTypes = useQuery({ queryKey: ["module-types"], queryFn: api.moduleTypes.list });
  const typeLabel = (code: string) =>
    moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US");
  const [step, setStep] = useState<1 | 2>(1);
  const [changeType, setChangeType] = useState<ChangeType | "">(correcting?.changeType ?? "");
  const [reason, setReason] = useState(correcting?.reason ?? "");
  const describedEnough = !needsDescription(changeType) || reason.trim() !== "";
  const [jiraUrl, setJiraUrl] = useState(correcting?.jiraUrl ?? "");
  const jiraInvalid = !isValidJiraUrl(jiraUrl);
  // `null` while the time typed is not a real one: that holds the form on step 1 rather
  // than quietly sending the time of submission instead.
  const [occurredAt, setOccurredAt] = useState<string | null>(correcting?.occurredAt ?? "");
  // No late-record note when correcting: the last change is the record itself, which the
  // correction replaces.
  const timeNote = occurredAtNote(occurredAt, archive, t, { lateRecords: !correcting });
  const [touched, setTouched] = useState<Record<string, Touched>>(correcting?.touched ?? {});
  const [added, setAdded] = useState<Added[]>(correcting?.added ?? []);
  const [query, setQuery] = useState("");
  // Opens on the critical parts — the ones with a serial, which is what this list is read
  // for — unless there are none (a machine filed without a BOM may have only counted
  // parts), where that would open on an empty list. `null` until the operator picks.
  // A correction opens on what it changed: those are the rows being looked at again.
  const [chosenKind, setKind] = useState<PartKind | null>(correcting ? "selected" : null);
  const kind: PartKind =
    chosenKind ?? (archive.items.some((item) => !isCounted(item)) ? "critical" : "all");
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  // Module slots opened onto the module's parts; closed at first, as on the archive page.
  const [openModules, setOpenModules] = useState<ReadonlySet<string>>(new Set());
  const toggleModule = (positionId: string) =>
    setOpenModules((current) => {
      const next = new Set(current);
      if (next.has(positionId)) next.delete(positionId);
      else next.add(positionId);
      return next;
    });
  const tableRef = useRef<HTMLDivElement | null>(null);
  const toggleFold = (positionId: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(positionId)) next.delete(positionId);
      else next.add(positionId);
      return next;
    });
  // An archive filed without a BOM has nothing for a part to be "outside of": its added
  // parts are just new positions, and a material change is just a material change.
  const bomWording = (key: string) => (archive.source_bom_id ? key : `${key}NoBom`);

  /** Write to a ticked row. */
  const edit = (item: ArchiveItem, patch: Partial<Touched> = {}) =>
    setTouched((previous) => ({
      ...previous,
      [item.position_id]: { ...(previous[item.position_id] ?? BLANK_TOUCHED), ...patch },
    }));

  const drop = (item: ArchiveItem) =>
    setTouched((previous) => {
      const next = { ...previous };
      delete next[item.position_id];
      return next;
    });

  const isKind = (item: ArchiveItem, option: PartKind) =>
    option === "all" ||
    (option === "selected"
      ? Boolean(touched[item.position_id])
      : (option === "critical") === !isCounted(item));

  // A part is found by whatever the operator has in front of them: the name on the
  // drawing, the number on the box, or the serial on the part itself.
  const needle = query.trim().toLowerCase();
  // The machine as the BOM tree it came from, foldable by level as on the archive page.
  const ordered = useMemo(() => inBomOrder(archive.items), [archive.items]);
  const nodes = useMemo(() => foldNodes(ordered), [ordered]);
  const parents = parentKeys(nodes);
  const depthOf = useMemo(() => new Map(nodes.map((node) => [node.key, node.depth])), [nodes]);
  // Every component under each one, so a group row can say how many of its parts are ticked
  // even while it is folded, and what replacing it does not cover.
  const descendants = useMemo(() => {
    const children = new Map<string, string[]>();
    for (const node of nodes) {
      if (node.parentKey)
        children.set(node.parentKey, [...(children.get(node.parentKey) ?? []), node.key]);
    }
    const below = (key: string): string[] =>
      (children.get(key) ?? []).flatMap((child) => [child, ...below(child)]);
    return new Map([...children.keys()].map((key) => [key, below(key)]));
  }, [nodes]);
  const narrowed = needle !== "" || kind !== "all";
  const hidden = narrowed ? new Set<string>() : hiddenKeys(nodes, folded);
  const { places, below: insideModule } = useMemo(() => innerRows(ordered), [ordered]);
  // The whole machine, modules' parts included: what the chips count and the lines name.
  const allItems = useMemo(
    () => [...archive.items, ...[...insideModule.values()].flat()],
    [archive.items, insideModule],
  );
  const matchesNeedle = (item: ArchiveItem) =>
    !needle ||
    [
      item.position_name,
      materialInfoOf(item),
      item.part_name,
      item.drawing_no,
      item.position_code,
      item.material_code,
      item.serial_raw,
      item.remark,
    ].some((field) => field?.toLowerCase().includes(needle));
  const isShown = (item: ArchiveItem) => {
    // A ticked row never disappears. Typing a serial into it would otherwise filter it out
    // from under the operator mid-keystroke, taking the input with it — and the same goes
    // for folding the line above it.
    if (touched[item.position_id]) return true;
    if (!isKind(item, kind)) return false;
    const place = places.get(item.position_id);
    // A module's part shows under its open slot — or wherever a search finds it. Not for a
    // filter alone: the list opens on the critical parts, which would open every module.
    if (place && !needle && !place.slots.every((slot) => openModules.has(slot))) return false;
    if (!place && hidden.has(item.position_id)) return false;
    return matchesNeedle(item);
  };
  // In tree order: each module's parts straight after its slot.
  const shown = ordered.flatMap((item) =>
    [item, ...(insideModule.get(item.position_id) ?? [])].filter(isShown),
  );
  const findItem = (positionId: string) =>
    allItems.find((candidate) => candidate.position_id === positionId);
  // §9.5 step 3: a module taken out cannot also be changed inside in the same submission.
  const conflicts = Object.keys(touched).filter((positionId) => {
    const item = findItem(positionId);
    return (
      item &&
      isModuleSlot(item) &&
      (insideModule.get(positionId) ?? []).some((inner) => touched[inner.position_id])
    );
  });

  // A serialized REPLACE without the serial that went in records nothing, and a counted line
  // must say a number of units that is actually fitted. The server says the same, in a
  // message that arrives after the round trip.
  // A substitute material is a new part number and the name that goes with it: one
  // without the other cannot be looked up later, so both are required once it is ticked.
  const materialIncomplete = (line: Touched) =>
    line.action === "REPLACE" &&
    line.substitute &&
    (!line.newMaterialCode.trim() || !line.newMaterialName.trim());
  const incomplete = Object.entries(touched).filter(([positionId, line]) => {
    const item = findItem(positionId);
    if (!item) return true;
    if (materialIncomplete(line)) return true;
    if (isCounted(item)) return lineQuantity(item, line) === null;
    return line.action === "REPLACE" && !line.newSerial.trim();
  });
  const missingMaterial = incomplete.some(([, line]) => materialIncomplete(line));
  const incompleteAdd = added.filter(
    (entry) => !entry.position_name.trim() || addedQuantity(entry) === null,
  );
  const lines = Object.values(touched);

  // Every serial going in, checked as it is entered (§7.5): installed elsewhere blocks the
  // submit; one this change takes off another slot is a swap; one entered twice is refused.
  const serialsIn: string[] = [];
  const released = new Map<string, string>();
  for (const [positionId, line] of Object.entries(touched)) {
    const item = findItem(positionId);
    if (!item || isModuleSlot(item) || isCounted(item)) continue;
    if (item.serial_normalized) released.set(item.serial_normalized, item.position_name);
    if (line.action === "REPLACE") serialsIn.push(line.newSerial);
  }
  for (const entry of added) serialsIn.push(entry.serial);
  const serialStatus = useSerialStatus(serialsIn);
  const entered = new Map<string, number>();
  for (const raw of serialsIn.filter(isUsableSerial)) {
    const serial = normalizeSerial(raw);
    entered.set(serial, (entered.get(serial) ?? 0) + 1);
  }
  const noteFor = (raw: string): NoteState | null => {
    const state = serialStatus.of(raw);
    if (!state) return null;
    const serial = normalizeSerial(raw);
    if ((entered.get(serial) ?? 0) > 1) return { kind: "duplicate" };
    const takenOff = released.get(serial);
    if (state.kind === "installed" && takenOff !== undefined) {
      return { kind: "swap", position: takenOff };
    }
    // Fitted by the very record being corrected, which the correction replaces.
    if (state.kind === "installed" && correcting?.ownSerials.has(serial)) return { kind: "free" };
    return state;
  };
  const serialBlocked = serialsIn.some((raw) => {
    const note = noteFor(raw);
    return note?.kind === "installed" || note?.kind === "duplicate";
  });

  // Every part going in must pass its material's barcode rules — the substitute's, when
  // the change names one: the label on the part is the material's, wherever it goes.
  const fittedMaterial = (item: ArchiveItem, line: Touched) =>
    line.substitute && line.newMaterialCode.trim()
      ? line.newMaterialCode.trim()
      : item.material_code;
  const partsIn: { materialCode: string | null; serial: string }[] = [];
  for (const [positionId, line] of Object.entries(touched)) {
    const item = findItem(positionId);
    if (!item || isModuleSlot(item) || isCounted(item) || line.action !== "REPLACE") continue;
    partsIn.push({ materialCode: fittedMaterial(item, line), serial: line.newSerial });
  }
  for (const entry of added)
    partsIn.push({ materialCode: entry.material_code, serial: entry.serial });
  const barcodeRules = useBarcodeRules(partsIn.map((part) => part.materialCode));
  const misfits = (materialCode: string | null | undefined, raw: string) =>
    isUsableSerial(raw) && !barcodeRules.accepts(materialCode, raw);
  const formatBlocked = partsIn.some((part) => misfits(part.materialCode, part.serial));

  // Every module going into a module slot, checked as it is entered (§9.5, §7 rule 3): it
  // must be a module of the slot's type and of this machine's product type, active, in
  // stock and complete — so an arm scanned into the head slot, or another robot model's arm,
  // is caught at the slot rather than refused after the round trip. A module this same
  // change takes out of another slot is moving, not fitted elsewhere; so is one the record
  // being corrected fitted.
  const leaving = new Set<string>();
  const moduleLines: { item: ArchiveItem; line: Touched }[] = [];
  for (const [positionId, line] of Object.entries(touched)) {
    const item = findItem(positionId);
    if (!item || !isModuleSlot(item)) continue;
    if (item.module) leaving.add(item.module.archive_id);
    if (line.action === "REPLACE") moduleLines.push({ item, line });
  }
  const moduleLookup = useModuleLookups(moduleLines.map(({ line }) => line.newSerial));
  const fittedTimes = new Map<string, number>();
  for (const { line } of moduleLines) {
    const state = moduleLookup(line.newSerial);
    if (state?.status !== "found") continue;
    if (correcting?.ownSerials.has(state.module.module_sn)) leaving.add(state.module.archive_id);
    fittedTimes.set(state.module.archive_id, (fittedTimes.get(state.module.archive_id) ?? 0) + 1);
  }
  const moduleNoteFor = (item: ArchiveItem, raw: string): ModuleNote | null => {
    const state = moduleLookup(raw);
    if (state?.status !== "found") return state;
    const module = state.module;
    if ((fittedTimes.get(module.archive_id) ?? 0) > 1) return { status: "duplicate", module };
    const problem = fitProblem(module, {
      moduleType: item.module_type ?? "",
      productTypeId: archive.product_type_id,
      leaving,
    });
    return problem ? { status: "refused", module, problem } : state;
  };
  const moduleBlocked = moduleLines.some(
    ({ item, line }) => moduleNoteFor(item, line.newSerial)?.status !== "found",
  );
  // The time typed on step 1 against the modules the picked parts sit in: a change inside
  // one versions it, and cannot predate its filing. The archive's own bound was checked on
  // step 1; this one depends on the parts, so it is checked here.
  const predated = filedAfter(
    occurredAt,
    Object.keys(touched).flatMap((positionId) => places.get(positionId)?.modules ?? []),
  );
  // A replacement built to another BOM version than the module it takes out may go in (§7
  // rule 5) — a newer arm on an older robot is a real repair — but it is said, at the slot
  // and beside the submit button, because it puts the slot outside the machine's BOM.
  const bomChangeFor = (item: ArchiveItem, raw: string): BomChange | null => {
    const note = moduleNoteFor(item, raw);
    if (note?.status !== "found" || !item.module) return null;
    if (note.module.source_bom_id === item.module.source_bom_id) return null;
    return { before: item.module.bom_version ?? null, after: note.module.bom_version };
  };
  const bomChanges = moduleLines.filter(({ item, line }) => bomChangeFor(item, line.newSerial));

  const replaceCount = lines.filter((line) => line.action === "REPLACE").length;
  const removeCount = lines.length - replaceCount;
  const lineCount = lines.length + added.length;
  // Anything typed or ticked, which closing or leaving would throw away: a change is not
  // kept as a draft, so this is the operator's one chance to keep it.
  const dirty =
    changeType !== "" ||
    reason.trim() !== "" ||
    jiraUrl.trim() !== "" ||
    occurredAt !== "" ||
    lineCount > 0;
  const [confirmingClose, setConfirmingClose] = useState(false);
  // Off while submitting: the page's own refresh after a successful submit must not ask.
  const blocker = useLeaveGuard(dirty && !pending);
  const requestClose = () => (dirty ? setConfirmingClose(true) : onClose());
  const canSubmit =
    lineCount > 0 &&
    incomplete.length === 0 &&
    incompleteAdd.length === 0 &&
    conflicts.length === 0 &&
    !serialBlocked &&
    !formatBlocked &&
    !moduleBlocked &&
    !predated &&
    !pending;
  // Per archive, once the change reaches into a module: 「整机：更换 1；W RBT-W-000101：更换 1」.
  const byArchive = new Map<string, { label: string; replaced: number; removed: number }>();
  for (const [positionId, line] of Object.entries(touched)) {
    const place = places.get(positionId);
    const key = place?.module.archive_id ?? archive.id;
    const group = byArchive.get(key) ?? {
      label: place
        ? `${place.module.module_type} ${place.module.archive_no}`
        : t("maintenance.thisArchive"),
      replaced: 0,
      removed: 0,
    };
    if (line.action === "REPLACE") group.replaced += 1;
    else group.removed += 1;
    byArchive.set(key, group);
  }

  /**
   * A scanner types the serial and then Enter. In a form, Enter submits — so the first scan
   * would send the event half-filled. It moves to the next ticked component still waiting
   * for a serial instead, which is what the next scan is for.
   */
  // An Enter an input method took arrives only as a keyup (scanEnter.ts).
  const serialEnter = scanEnterHandlers<HTMLInputElement>(
    (here) => {
      const inputs = [
        ...(tableRef.current?.querySelectorAll<HTMLInputElement>("input[data-scan]") ?? []),
      ];
      const after = inputs.slice(inputs.indexOf(here) + 1);
      after.find((input) => !input.value && !input.disabled)?.focus();
    },
    { burst: true },
  );

  function submit() {
    const lines: MaintenanceSubmission["lines"] = [];
    for (const [positionId, line] of Object.entries(touched)) {
      const item = findItem(positionId);
      if (!item) continue;
      const place = places.get(positionId);
      const counted = isCounted(item);
      // Only on a REPLACE that says it fitted something else, and only when filled: the
      // server refuses the field on the other actions, and nothing means "the BOM's part".
      const substitute = line.action === "REPLACE" && line.substitute;
      lines.push({
        line_no: lines.length + 1,
        action: line.action,
        position_id: positionId,
        new_position: null,
        // Never on a counted position: it has no serial, and sending one — even the empty
        // string a blank field would produce — is what NEW_SERIAL_NOT_ALLOWED refuses.
        old_serial: counted ? null : item.serial_raw,
        new_serial: !counted && line.action === "REPLACE" ? line.newSerial.trim() : null,
        new_material_code: substitute ? line.newMaterialCode.trim() || null : null,
        new_material_name: substitute ? line.newMaterialName.trim() || null : null,
        // `incomplete` has already refused a null here, so the fallback is never taken.
        quantity: lineQuantity(item, line) ?? item.quantity,
        ...(place ? { target_archive_id: place.module.archive_id } : {}),
      });
    }
    for (const entry of added) {
      lines.push({
        line_no: lines.length + 1,
        action: "ADD",
        position_id: null,
        new_material_code: null,
        new_material_name: null,
        new_position: {
          position_name: entry.position_name.trim(),
          // The BOM has nothing to say about this part, so its name and number are the
          // only record of what was fitted. Blank becomes null: "" is a material number
          // that is present and says nothing.
          material_code: entry.material_code.trim() || null,
        },
        old_serial: null,
        new_serial: entry.serial.trim() || null,
        quantity: addedQuantity(entry) ?? 1,
      });
    }
    if (!changeType) return;
    onSubmit({
      changeType,
      reason: reason.trim() || null,
      jiraUrl: jiraUrl.trim() || null,
      occurredAt: occurredAt || null,
      lines,
    });
  }

  const foldButton = (item: ArchiveItem) => {
    const isFolded = folded.has(item.position_id);
    const label = t(isFolded ? "tree.expand" : "tree.collapse", { name: item.position_name });
    return (
      <button
        type="button"
        className={
          isFolded ? "row-toggle tooltip-trigger" : "row-toggle row-toggle-open tooltip-trigger"
        }
        aria-expanded={!isFolded}
        aria-label={label}
        data-tooltip={label}
        onClick={() => toggleFold(item.position_id)}
      >
        <ChevronIcon size={14} />
      </button>
    );
  };

  return (
    <Modal
      title={correcting?.title ?? t("maintenance.start")}
      wide
      onClose={requestClose}
      onSubmit={() => (step === 1 ? setStep(2) : submit())}
      footer={
        step === 1 ? (
          <>
            <button
              type="submit"
              className="button-primary"
              disabled={
                !changeType ||
                !describedEnough ||
                jiraInvalid ||
                occurredAt === null ||
                timeNote.blocked
              }
            >
              {t("common.next")}
            </button>
            <button type="button" onClick={requestClose}>
              {t("common.cancel")}
            </button>
          </>
        ) : (
          <>
            {/* What will be submitted, always in view: exactly the ticked rows and the
                added parts, however far down the list the operator has scrolled. */}
            <span className="parts-summary" aria-live="polite">
              {conflicts.length > 0 ? (
                <span className="hint hint-error">{t("maintenance.moduleConflict")}</span>
              ) : serialBlocked ? (
                <span className="hint hint-error">{t("maintenance.serialBlocked")}</span>
              ) : formatBlocked ? (
                <span className="hint hint-error">{t("maintenance.formatBlocked")}</span>
              ) : moduleBlocked ? (
                <span className="hint hint-error">{t("maintenance.moduleBlocked")}</span>
              ) : predated ? (
                <span className="hint hint-error">
                  {t("maintenance.occurredBeforeModule", {
                    archive: predated.archive_no,
                    time: new Date(predated.created_at ?? "").toLocaleString(),
                  })}
                </span>
              ) : byArchive.size > 1 ? (
                [...byArchive.values()]
                  .map((group) =>
                    t("maintenance.archiveSummary", {
                      archive: group.label,
                      replaced: group.replaced,
                      removed: group.removed,
                    }),
                  )
                  .join("；")
              ) : incomplete.length > 0 ? (
                <span className="hint hint-error">
                  {t(
                    missingMaterial
                      ? "maintenance.substituteNeedsBoth"
                      : "maintenance.replaceNeedsSerial",
                  )}
                </span>
              ) : (
                t("maintenance.selectedSummary", {
                  n: lineCount,
                  // Not `replace`: i18next reads that option as the interpolation values.
                  replaced: replaceCount,
                  removed: removeCount,
                  added: added.length,
                })
              )}
              <span className="hint">
                {" "}
                · v{archive.current_version} → v{archive.current_version + 1}
              </span>
              {bomChanges.length > 0 && (
                <span className="hint hint-warn">
                  {" "}
                  {t("maintenance.moduleBomChangedSummary", { n: bomChanges.length })}
                </span>
              )}
            </span>
            <button type="button" onClick={() => setStep(1)}>
              {t("common.back")}
            </button>
            <button type="submit" className="button-primary" disabled={!canSubmit}>
              {t(correcting ? "maintenance.saveCorrection" : "maintenance.submit")}
            </button>
          </>
        )
      }
    >
      {!correcting && (
        <PendingCorrectionNotice
          archiveIds={[archive.id, ...(archive.installed_on ?? []).map((link) => link.archive_id)]}
          onOpen={() => {
            // With work in the form the router holds the way out and asks first.
            if (!dirty) onClose();
          }}
        />
      )}

      <ol className="steps">
        <li className={step === 1 ? "is-current" : "is-done"}>{t("maintenance.stepReason")}</li>
        <li className={step === 2 ? "is-current" : ""}>{t("maintenance.stepParts")}</li>
      </ol>

      {step === 1 ? (
        <>
          <div className="form-row">
            <label className="required" htmlFor="change-type">
              {t("maintenance.changeType")}
            </label>
            <ChangeTypeSelect id="change-type" value={changeType} onChange={setChangeType} />
          </div>
          <div className="form-row">
            {/* Required only for 其他, which has nothing else to say what was done. */}
            <label
              className={needsDescription(changeType) ? "required" : undefined}
              htmlFor="change-reason"
            >
              {t("maintenance.reason")}
            </label>
            <input
              id="change-reason"
              required={needsDescription(changeType)}
              placeholder={t("maintenance.reasonPlaceholder")}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <div className="form-row">
            <label htmlFor="change-jira">{t("maintenance.jiraUrl")}</label>
            <input
              id="change-jira"
              type="url"
              inputMode="url"
              placeholder={t("maintenance.jiraUrlPlaceholder")}
              aria-invalid={jiraInvalid}
              aria-describedby="change-jira-hint"
              value={jiraUrl}
              onChange={(event) => setJiraUrl(event.target.value)}
            />
          </div>
          <div className="form-row">
            <span className="form-row-spacer" />
            <small className={jiraInvalid ? "hint hint-error" : "hint"} id="change-jira-hint">
              {t(jiraInvalid ? "maintenance.jiraUrlInvalid" : "maintenance.jiraUrlHint")}
            </small>
          </div>
          <div className="form-row">
            <label htmlFor="change-occurred">{t("maintenance.occurredAt")}</label>
            {/* Not datetime-local: Chrome and Safari draw that in the browser's language
                (年/月/日 on an English page). */}
            <DateTimeField
              id="change-occurred"
              describedBy="change-occurred-hint"
              value={occurredAt ?? ""}
              onChange={setOccurredAt}
            />
          </div>
          <div className="form-row">
            <span className="form-row-spacer" />
            {/* Left empty it is sent as the moment of submission, not of opening the form: a
                form held open while the work is done would otherwise date it too early. */}
            <small className={timeNote.className} id="change-occurred-hint">
              {timeNote.text ?? t("maintenance.occurredAtHint")}
              <br />
              {t("datetime.timeZone", timeZoneLabel())}
            </small>
          </div>
        </>
      ) : (
        <>
          {/* Three steps, one per line: in one run-on sentence the operator had to
              parse which clause applied to the action in hand. */}
          <ol className="hint hint-steps">
            <li>{t("maintenance.partsStepPick")}</li>
            <li>{t("maintenance.partsStepFill")}</li>
            <li>{t(bomWording("maintenance.partsStepAdd"))}</li>
          </ol>

          {/* Two rows: the search gets the whole width, so its prompt is not cut off in
              English, and the filters and actions sit together under it. */}
          <div className="parts-toolbar">
            <div className="parts-toolbar-row">
              <label className="visually-hidden" htmlFor="parts-filter">
                {t("common.search")}
              </label>
              <input
                id="parts-filter"
                type="search"
                autoComplete="off"
                placeholder={t("maintenance.findPart")}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <span className="muted">
                {t("maintenance.shownParts", { n: shown.length, total: allItems.length })}
              </span>
            </div>
            <div className="parts-toolbar-row">
              {/* The archive page's chips, counted over the whole machine, plus the ticked
                  ones — to check this change in one place before submitting it. */}
              <div className="chip-group" role="group" aria-label={t("archive.showRows")}>
                {PART_KINDS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={kind === option ? "chip chip-on" : "chip"}
                    aria-pressed={kind === option}
                    onClick={() => setKind(option)}
                  >
                    {option === "selected"
                      ? t("maintenance.filterSelected", { n: lines.length })
                      : t(`archive.filter.${option}`, {
                          n: allItems.filter((item) => isKind(item, option)).length,
                        })}
                  </button>
                ))}
              </div>
              <FoldControls
                levels={levelCount(nodes)}
                disabled={narrowed}
                onLevel={(level) =>
                  setFolded(level === null ? new Set() : foldedToLevel(nodes, level))
                }
              />
              {/* §8.5: a part the BOM never had is allowed, and kept out of the main list
                  so it is chosen on purpose rather than by accident. */}
              <button
                type="button"
                className="parts-toolbar-add"
                onClick={() => setAdded([{ ...BLANK_ADDED }, ...added])}
              >
                <AddLabel>{t(bomWording("maintenance.addOutOfBom"))}</AddLabel>
              </button>
            </div>
          </div>

          {/* Above the list, where the button that made it is: below four hundred rows a new
              card would be out of sight. */}
          {added.map((entry, index) => {
            const patch = (fields: Partial<Added>) =>
              setAdded(added.map((other, i) => (i === index ? { ...other, ...fields } : other)));
            return (
              // A card per part rather than one long row: four fields on a line push the
              // last of them off the dialog, and these are read as one part's description.
              <div className="added-part" key={index}>
                <div className="form-row">
                  <label className="required" htmlFor={`add-name-${index}`}>
                    {t(bomWording("maintenance.newPosition"))}
                  </label>
                  <input
                    id={`add-name-${index}`}
                    required
                    placeholder={t("archive.position")}
                    value={entry.position_name}
                    onChange={(event) => patch({ position_name: event.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setAdded(added.filter((_, i) => i !== index))}
                  >
                    {t("common.delete")}
                  </button>
                </div>
                <div className="form-row">
                  <label htmlFor={`add-material-code-${index}`}>{t("archive.materialCode")}</label>
                  <input
                    id={`add-material-code-${index}`}
                    className="mono"
                    value={entry.material_code}
                    onChange={(event) => patch({ material_code: event.target.value })}
                  />
                </div>
                <div className="form-row">
                  <label htmlFor={`add-serial-${index}`}>{t("archive.serial")}</label>
                  <input
                    id={`add-serial-${index}`}
                    className="serial"
                    value={entry.serial}
                    onChange={(event) => patch({ serial: event.target.value })}
                  />
                  {misfits(entry.material_code, entry.serial) ? (
                    <BarcodeMismatchNote
                      materialCode={entry.material_code}
                      patterns={barcodeRules.patternsOf(entry.material_code)}
                    />
                  ) : (
                    <SerialStatusNote state={noteFor(entry.serial)} />
                  )}
                  <label htmlFor={`add-quantity-${index}`}>{t("archive.quantity")}</label>
                  {/* Fixed at one while a serial is filled in: a serial is one unit. */}
                  <input
                    id={`add-quantity-${index}`}
                    className="added-quantity"
                    type="number"
                    min={1}
                    step={1}
                    disabled={Boolean(entry.serial.trim())}
                    value={entry.serial.trim() ? "1" : entry.quantity}
                    onChange={(event) => patch({ quantity: event.target.value })}
                  />
                </div>
              </div>
            );
          })}

          <div ref={tableRef}>
            <ResizableTable
              storageKey="maintenance-parts-2"
              className="parts"
              wrapClassName="parts-scroll"
            >
              <thead>
                <tr>
                  <th className="parts-pick">
                    <span className="visually-hidden">{t("maintenance.pick")}</span>
                  </th>
                  {/* As on the BOM and the record: where on the machine, first. */}
                  <th className="col-part-name">{t("bom.partName")}</th>
                  <th>{t("archive.position")}</th>
                  <th>{t("bom.drawingNo")}</th>
                  <th>{t("maintenance.currentlyFitted")}</th>
                  <th className="parts-remark">{t("bom.remark")}</th>
                  <th>{t("maintenance.action")}</th>
                  <th>{t("maintenance.newSerialOrCount")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.flatMap((item) => {
                  const line = touched[item.position_id];
                  const checkboxId = `pick-${item.position_id}`;
                  const isGroup = parents.has(item.position_id);
                  const below = descendants.get(item.position_id) ?? [];
                  const pickedBelow = below.filter((key) => touched[key]).length;
                  const counted = isCounted(item);
                  const place = places.get(item.position_id);
                  const moduleSlot = isModuleSlot(item);
                  const hasInside = (insideModule.get(item.position_id) ?? []).length > 0;
                  const moduleOpen = openModules.has(item.position_id);
                  // Quantity 0: the part was taken off — marked as on the archive's own list.
                  const removed = item.quantity === 0;
                  const rowClass = [
                    line ? "is-touched" : "is-selectable",
                    place ? "module-child" : "",
                    isGroup ? "is-group" : "",
                    // Fitted with a part the BOM does not specify, as on the archive's list.
                    item.off_bom ? "is-off-bom" : "",
                    removed ? "is-removed" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const row = (
                    <tr
                      key={item.position_id}
                      className={rowClass}
                      // A click anywhere on an unticked row ticks it — the checkbox is a
                      // small target on a shop-floor screen. It never unticks: a stray click
                      // in a half-filled row must not throw away what was typed, so that is
                      // left to the checkbox. Clicks on the row's own controls (and the
                      // name, which is the checkbox's label) are theirs.
                      onClick={(event) => {
                        if (line) return;
                        if (
                          (event.target as HTMLElement).closest(
                            "input, select, textarea, button, label, a",
                          )
                        )
                          return;
                        edit(item);
                      }}
                    >
                      <td className="parts-pick">
                        <input
                          id={checkboxId}
                          type="checkbox"
                          checked={Boolean(line)}
                          onChange={() => (line ? drop(item) : edit(item))}
                        />
                      </td>
                      <td>{item.part_name || <span className="muted">—</span>}</td>
                      <td
                        className="parts-name"
                        // Indented by BOM level, so a group's parts read as inside it.
                        style={{
                          paddingLeft:
                            8 +
                            (place
                              ? place.level * 24
                              : narrowed
                                ? 0
                                : (depthOf.get(item.position_id) ?? 0) * 16),
                        }}
                      >
                        <span className="parts-name-inner">
                          {hasInside ? (
                            <button
                              type="button"
                              className={moduleOpen ? "row-toggle row-toggle-open" : "row-toggle"}
                              aria-expanded={moduleOpen}
                              aria-label={t(
                                moduleOpen
                                  ? "moduleArchive.hideContents"
                                  : "moduleArchive.showContents",
                                { archiveNo: item.module?.archive_no ?? "" },
                              )}
                              onClick={() => toggleModule(item.position_id)}
                            >
                              <ChevronIcon size={14} />
                            </button>
                          ) : isGroup && !narrowed && !place ? (
                            foldButton(item)
                          ) : (
                            <span className="row-toggle-spacer" />
                          )}
                          {/* The name is the checkbox's label: the bigger target on a
                              shop-floor screen, and what a screen reader announces. Two
                              lines at most; the full name is in the tooltip. */}
                          <label
                            htmlFor={checkboxId}
                            className="parts-name-text"
                            title={materialInfoOf(item)}
                          >
                            {materialInfoOf(item)}
                          </label>
                          {isGroup && (
                            // Every group row here is itself a fitted component (it has a
                            // position of its own), so it is marked as one that can be
                            // ticked — and how many of its parts already are.
                            <span
                              className="badge badge-group"
                              title={t("maintenance.groupHint", { n: below.length })}
                            >
                              {t("maintenance.groupBadge", { n: below.length })}
                            </span>
                          )}
                          {pickedBelow > 0 && (
                            <span className="badge badge-picked">
                              {t("maintenance.pickedBelow", { n: pickedBelow })}
                            </span>
                          )}
                          {moduleSlot && item.module_type && (
                            <span className="module-badge">{item.module_type}</span>
                          )}
                          {/* Whose part it is: the line goes to that module's archive. */}
                          {place && (
                            <span className="hint module-owner">{place.module.archive_no}</span>
                          )}
                        </span>
                      </td>
                      <td className="mono">
                        {item.drawing_no || <span className="muted">—</span>}
                      </td>
                      {/* What is fitted: the unit (or count), then the material number the
                          replacement is compared against. Its 物料描述 is the name column. */}
                      <td className="current-part">
                        <span className={removed ? "serial quantity-removed" : "serial"}>
                          {item.serial_raw ?? `× ${item.quantity}`}
                        </span>
                        {removed && (
                          <span className="badge badge-removed" title={t("archive.removedHint")}>
                            {t("archive.removed")}
                          </span>
                        )}
                        {item.material_code && (
                          <span className="current-part-material">
                            <span className="mono">{item.material_code}</span>
                            {item.off_bom && (
                              <span className="badge badge-off-bom">{t("archive.offBom")}</span>
                            )}
                          </span>
                        )}
                      </td>
                      {/* Next to what is fitted, which it qualifies: "肩" says which of three
                          identical motors this row is, before the operator ticks it. */}
                      <td className="parts-remark">{item.remark ?? ""}</td>
                      {line ? (
                        <>
                          <td>
                            <select
                              aria-label={`action-${item.position_code}`}
                              value={line.action}
                              onChange={(event) =>
                                edit(item, { action: event.target.value as Action })
                              }
                            >
                              <option value="REPLACE">{t("maintenance.replace")}</option>
                              <option value="REMOVE">{t("maintenance.remove")}</option>
                            </select>
                          </td>
                          <td>
                            {counted ? (
                              // How many of them — for a removal too, which is exactly when
                              // the count matters most: it is what the position is left
                              // holding. The current count is beside it, so the limit is
                              // read rather than guessed.
                              <span className="count-input">
                                <label htmlFor={`quantity-${item.position_id}`}>
                                  {t(
                                    line.action === "REMOVE"
                                      ? "maintenance.removeQuantity"
                                      : "maintenance.replaceQuantity",
                                  )}
                                </label>
                                <input
                                  id={`quantity-${item.position_id}`}
                                  aria-label={`quantity-${item.position_code}`}
                                  type="number"
                                  inputMode="numeric"
                                  min={1}
                                  max={item.quantity}
                                  value={line.quantity ?? String(item.quantity)}
                                  onChange={(event) => edit(item, { quantity: event.target.value })}
                                />
                                <span className="hint">
                                  {t("maintenance.currentQuantity", { n: item.quantity })}
                                </span>
                              </span>
                            ) : line.action === "REPLACE" ? (
                              <>
                                <label
                                  className="field-label required"
                                  htmlFor={`new-serial-${item.position_id}`}
                                >
                                  {t(
                                    moduleSlot
                                      ? "maintenance.fittedModule"
                                      : "maintenance.fittedSerial",
                                  )}
                                </label>
                                <input
                                  id={`new-serial-${item.position_id}`}
                                  required
                                  aria-label={`new-serial-${item.position_code}`}
                                  className="serial"
                                  data-scan=""
                                  placeholder={t(
                                    moduleSlot
                                      ? "maintenance.newModulePlaceholder"
                                      : "maintenance.newSerialPlaceholder",
                                  )}
                                  value={line.newSerial}
                                  onChange={(event) =>
                                    edit(item, { newSerial: event.target.value })
                                  }
                                  {...serialEnter}
                                />
                                {moduleSlot ? (
                                  <>
                                    <ModuleFitNote
                                      note={moduleNoteFor(item, line.newSerial)}
                                      label={typeLabel}
                                    />
                                    <BomChangeNote change={bomChangeFor(item, line.newSerial)} />
                                  </>
                                ) : misfits(fittedMaterial(item, line), line.newSerial) ? (
                                  <BarcodeMismatchNote
                                    materialCode={fittedMaterial(item, line)}
                                    patterns={barcodeRules.patternsOf(fittedMaterial(item, line))}
                                  />
                                ) : (
                                  <SerialStatusNote state={noteFor(line.newSerial)} />
                                )}
                              </>
                            ) : (
                              <span className="muted">—</span>
                            )}
                            {/* Under the serial or count it qualifies, in the same cell:
                                most replacements fit the BOM's part, so this stays one
                                checkbox until it is needed. */}
                            {/* A module is a module: what goes in is identified by its own
                                archive, not by a substitute part number. */}
                            {line.action === "REPLACE" && !moduleSlot && (
                              <div className="material-change">
                                <label className="substitute-toggle">
                                  <input
                                    type="checkbox"
                                    aria-label={`substitute-${item.position_code}`}
                                    checked={line.substitute}
                                    onChange={(event) =>
                                      edit(item, { substitute: event.target.checked })
                                    }
                                  />
                                  {t("maintenance.useSubstitute")}
                                </label>
                                {line.substitute && (
                                  <>
                                    {/* Left blank rather than pre-filled with what is fitted
                                        now, which would send an "override" identical to the
                                        current value. */}
                                    <label
                                      className="field-label required"
                                      htmlFor={`new-material-code-${item.position_id}`}
                                    >
                                      {t("maintenance.newMaterialCode")}
                                    </label>
                                    <input
                                      id={`new-material-code-${item.position_id}`}
                                      required
                                      aria-label={`new-material-code-${item.position_code}`}
                                      className="mono"
                                      placeholder={t("maintenance.newMaterialCode")}
                                      value={line.newMaterialCode}
                                      onChange={(event) =>
                                        edit(item, { newMaterialCode: event.target.value })
                                      }
                                    />
                                    <label
                                      className="field-label required"
                                      htmlFor={`new-material-name-${item.position_id}`}
                                    >
                                      {t("maintenance.newMaterialName")}
                                    </label>
                                    <input
                                      id={`new-material-name-${item.position_id}`}
                                      required
                                      aria-label={`new-material-name-${item.position_code}`}
                                      placeholder={t("maintenance.newMaterialName")}
                                      value={line.newMaterialName}
                                      onChange={(event) =>
                                        edit(item, { newMaterialName: event.target.value })
                                      }
                                    />
                                    <small className="hint">
                                      {t(bomWording("maintenance.substituteHint"))}
                                    </small>
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        </>
                      ) : (
                        // Unticked: nothing to fill, so nothing that looks like it will be sent.
                        <td colSpan={2} />
                      )}
                    </tr>
                  );
                  if (!line || !isGroup) return [row];
                  // Replacing a group records that one component; the server does not touch
                  // the positions under it. Said where it is ticked, since "整个关节换了"
                  // can mean either.
                  return [
                    row,
                    <tr key={`${item.position_id}-note`} className="parts-note">
                      <td />
                      <td colSpan={7} className="hint">
                        {t("maintenance.groupNote", { name: item.position_name, n: below.length })}
                      </td>
                    </tr>,
                  ];
                })}
              </tbody>
            </ResizableTable>
          </div>
        </>
      )}

      {error}
      <ScrappedSerialPrompt
        serials={serialsIn}
        status={serialStatus}
        onRemove={(serial) => {
          setTouched((current) =>
            Object.fromEntries(
              Object.entries(current).map(([positionId, line]) => [
                positionId,
                normalizeSerial(line.newSerial) === serial ? { ...line, newSerial: "" } : line,
              ]),
            ),
          );
          setAdded((current) =>
            current.map((entry) =>
              normalizeSerial(entry.serial) === serial ? { ...entry, serial: "" } : entry,
            ),
          );
        }}
      />
      {(confirmingClose || blocker.state === "blocked") && (
        <Modal
          title={t("maintenance.leaveTitle")}
          onClose={() => {
            setConfirmingClose(false);
            if (blocker.state === "blocked") blocker.reset();
          }}
          footer={
            <>
              <button
                type="button"
                className="button-primary"
                data-autofocus
                onClick={() => {
                  setConfirmingClose(false);
                  if (blocker.state === "blocked") blocker.reset();
                }}
              >
                {t("maintenance.leaveKeepEditing")}
              </button>
              <button
                type="button"
                className="button-danger"
                onClick={() => {
                  setConfirmingClose(false);
                  if (blocker.state === "blocked") blocker.proceed();
                  onClose();
                }}
              >
                {t("maintenance.leaveDiscard")}
              </button>
            </>
          }
        >
          <p>{t("maintenance.leaveBody", { n: lineCount })}</p>
        </Modal>
      )}
    </Modal>
  );
}

/** A module going in built to another BOM version than the one coming out. */
interface BomChange {
  before: string | null;
  after: string | null;
}

/** Under a module slot's box, below the fit: the two BOM versions, when they differ. */
function BomChangeNote({ change }: { change: BomChange | null }) {
  const { t } = useTranslation();
  if (!change) return null;
  const none = t("maintenance.moduleBomNone");
  return (
    <span className="serial-note is-used">
      {t("maintenance.moduleBomChanged", {
        after: change.after ?? none,
        before: change.before ?? none,
      })}
    </span>
  );
}

/** Under a module slot's box: the module the code names, or why it cannot go in. */
function ModuleFitNote({
  note,
  label,
}: {
  note: ModuleNote | null;
  label: (code: string) => string;
}) {
  const { t } = useTranslation();
  if (!note) return null;
  switch (note.status) {
    case "checking":
      return <span className="serial-note muted">{t("moduleFiling.checking")}</span>;
    case "found":
      return (
        <span className="serial-note is-free">
          {t("maintenance.moduleFits", { archiveNo: note.module.archive_no })}
        </span>
      );
    case "duplicate":
      return (
        <span className="serial-note is-installed">
          {t("maintenance.moduleDuplicate", { archiveNo: note.module.archive_no })}
        </span>
      );
    case "refused":
      return (
        <span className="serial-note is-installed">{problemText(note.problem, label, t)}</span>
      );
    case "not_found":
      return <span className="serial-note is-installed">{t("moduleFiling.notFound")}</span>;
    case "failed":
      return (
        <span className="serial-note is-installed">
          {note.error instanceof ApiError
            ? note.error.localizedMessage
            : t("moduleFiling.lookupFailed")}
        </span>
      );
  }
}
